import React, { useEffect, useMemo, useRef, useState } from 'react';

function parseMeetingCount(html) {
  const countMatch = String(html || '').match(/MEETING COUNT\s*:?\s*(\d+)/i);
  if (countMatch) {
    return Math.max(1, Math.min(8, Number(countMatch[1])));
  }
  const meetings = String(html || '').match(/MEETING\s+[1-8]/gi) || [];
  const nums = meetings.map((item) => Number(item.replace(/\D/g, ''))).filter((n) => n >= 1);
  return nums.length ? Math.max(1, Math.min(8, Math.max(...nums))) : 3;
}

export default function LegendParleyDebrief({
  apiFetch,
  sessionId,
  htmlReport,
  disabled = false,
  onHtmlUpdated,
  onError,
}) {
  const meetingCount = useMemo(() => parseMeetingCount(htmlReport), [htmlReport]);
  const [meetingNumber, setMeetingNumber] = useState(1);
  const [notes, setNotes] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const [recording, setRecording] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setMeetingNumber((current) => Math.min(current, meetingCount));
  }, [meetingCount]);

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
    recorderRef.current = null;
    setRecording(false);
  };

  const startRecording = async () => {
    if (disabled || submitting || !navigator.mediaDevices?.getUserMedia) {
      onError?.('This browser cannot record audio. Upload a voice note instead.');
      return;
    }
    onError?.(null);
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    recorder.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
      const file = new File([blob], `meeting-${meetingNumber}-debrief.webm`, { type: blob.type || 'audio/webm' });
      setAudioFile(file);
    };
    recorderRef.current = recorder;
    recorder.start();
    setRecording(true);
  };

  const handleSubmit = async () => {
    if (submitting || disabled) return;
    if (!notes.trim() && !audioFile) {
      onError?.('Record a voice note, upload audio, or type what happened in the meeting.');
      return;
    }
    setSubmitting(true);
    onError?.(null);
    try {
      const body = new FormData();
      body.append('session_id', sessionId);
      body.append('meeting_number', String(meetingNumber));
      if (notes.trim()) body.append('notes', notes.trim());
      if (audioFile) body.append('file', audioFile, audioFile.name);
      const response = await apiFetch('/api/reports/legend/debrief', {
        method: 'POST',
        body,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Could not update the playbook from this debrief.');
      }
      const payload = await response.json();
      const html = payload.report?.html_content || payload.html_content;
      if (!html) {
        throw new Error('Debrief saved but no updated playbook was returned.');
      }
      onHtmlUpdated?.({
        html,
        title: payload.title || payload.report?.title,
        report: payload.report,
      });
      setNotes('');
      setAudioFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      onError?.(err.message || 'Debrief failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-[#3a342c] rounded-md bg-[#101418] p-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-[#c4a35a]">After-meeting debrief</h3>
        <p className="text-xs text-[#6f675c] mt-1">
          Record or upload a voice note after each meeting. Those thoughts update later meeting playbooks.
        </p>
      </div>
      <label className="block space-y-1">
        <span className="text-xs uppercase tracking-[0.16em] text-[#8A8070]">This debrief is for</span>
        <select
          value={meetingNumber}
          onChange={(event) => setMeetingNumber(Number(event.target.value))}
          disabled={disabled || submitting}
          className="w-full px-3 py-2 bg-[#14181d] border border-[#3a342c] rounded-md text-[#e8e2d6]"
        >
          {Array.from({ length: meetingCount }, (_, idx) => idx + 1).map((num) => (
            <option key={num} value={num}>
              Meeting {num}
            </option>
          ))}
        </select>
      </label>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={disabled || submitting}
          onClick={recording ? stopRecording : startRecording}
          className="px-3 py-2 border border-[#c4a35a]/60 text-[#c4a35a] rounded-md text-sm disabled:opacity-40"
        >
          {recording ? 'Stop recording' : 'Record voice note'}
        </button>
        <button
          type="button"
          disabled={disabled || submitting}
          onClick={() => fileInputRef.current?.click()}
          className="px-3 py-2 border border-[#3a342c] text-[#e8e2d6] rounded-md text-sm disabled:opacity-40"
        >
          Upload audio
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,.webm,.mp3,.m4a,.wav,.ogg"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) setAudioFile(file);
          }}
        />
      </div>
      {audioFile && (
        <p className="text-xs text-[#c4a35a] truncate">Voice note ready: {audioFile.name}</p>
      )}
      <textarea
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        rows={4}
        disabled={disabled || submitting}
        className="w-full px-3 py-2.5 bg-[#14181d] border border-[#3a342c] rounded-md text-[#e8e2d6] focus:outline-none focus:border-[#c4a35a]"
        placeholder="Or type what happened, what they revealed, and what you want to change next time."
      />
      <button
        type="button"
        onClick={handleSubmit}
        disabled={disabled || submitting}
        className="px-4 py-2.5 bg-[#c4a35a] text-[#14110c] font-semibold rounded-md hover:bg-[#d4b56d] disabled:opacity-40"
      >
        {submitting ? 'Updating later meetings…' : 'Work this into future meetings'}
      </button>
    </div>
  );
}
